import * as React from 'react';
import { MapPin, TrendingUp, Clock, Search } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { LoanLimit, LoanLimitsData } from './types';
import loanLimitsData from '@/data/loan-limits-2026.json';

const RECENT_SEARCHES_KEY = 'lendwise-loan-limits-recent';
const MAX_RECENT = 5;
const MIN_SEARCH_LENGTH = 2;
const MAX_RESULTS = 100;

// Reuse formatter instance to avoid per-call allocation
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

function formatCurrency(value: number): string {
  return currencyFormatter.format(value);
}

function getRecentSearches(): string[] {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    if (!stored) {
      return [];
    }
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function saveRecentSearch(id: string): void {
  try {
    const recent = getRecentSearches().filter((r) => r !== id);
    recent.unshift(id);
    localStorage.setItem(
      RECENT_SEARCHES_KEY,
      JSON.stringify(recent.slice(0, MAX_RECENT))
    );
  } catch {
    // Ignore localStorage errors
  }
}

interface LoanLimitsLookupProps {
  className?: string;
  onSelect?: (limit: LoanLimit) => void;
}

export function LoanLimitsLookup({ className, onSelect }: LoanLimitsLookupProps) {
  const [query, setQuery] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);
  const [selectedLimit, setSelectedLimit] = React.useState<LoanLimit | null>(null);
  const [recentIds, setRecentIds] = React.useState<string[]>([]);
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const parentRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const blurTimeoutRef = React.useRef<number>();

  const data = loanLimitsData as LoanLimitsData;

  React.useEffect(() => {
    setRecentIds(getRecentSearches());
  }, []);

  // Cleanup blur timeout on unmount
  React.useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  // Filter limits based on query
  const { filteredLimits, totalMatches } = React.useMemo(() => {
    if (query.length < MIN_SEARCH_LENGTH) {
      return { filteredLimits: [], totalMatches: 0 };
    }
    const q = query.toLowerCase();
    const matches = data.limits.filter(
      (l) =>
        l.county.toLowerCase().includes(q) ||
        l.state.toLowerCase().includes(q) ||
        `${l.county}, ${l.state}`.toLowerCase().includes(q)
    );
    return {
      filteredLimits: matches.slice(0, MAX_RESULTS),
      totalMatches: matches.length
    };
  }, [query, data.limits]);

  const isResultsCapped = totalMatches > MAX_RESULTS;

  // Get recent limits for display when no query
  const recentLimits = React.useMemo(() => {
    return recentIds
      .map((id) => data.limits.find((l) => l.id === id))
      .filter((l): l is LoanLimit => l !== undefined);
  }, [recentIds, data.limits]);

  // Determine what to show in dropdown
  const displayItems = query.length >= MIN_SEARCH_LENGTH ? filteredLimits : recentLimits;
  const showDropdown = isFocused && (displayItems.length > 0 || query.length >= MIN_SEARCH_LENGTH);

  // Virtualization for the results list
  const rowVirtualizer = useVirtualizer({
    count: displayItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 44,
    overscan: 5
  });

  // Reset highlighted index when query or display items change
  React.useEffect(() => {
    setHighlightedIndex(-1);
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((i) => {
          const next = Math.min(i + 1, displayItems.length - 1);
          rowVirtualizer.scrollToIndex(next, { align: 'nearest' });
          return next;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((i) => {
          const next = Math.max(i - 1, 0);
          rowVirtualizer.scrollToIndex(next, { align: 'nearest' });
          return next;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && displayItems[highlightedIndex]) {
          handleSelect(displayItems[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsFocused(false);
        inputRef.current?.blur();
        break;
    }
  };

  const handleSelect = (limit: LoanLimit) => {
    setSelectedLimit(limit);
    setQuery('');
    setIsFocused(false);
    setHighlightedIndex(-1);
    saveRecentSearch(limit.id);
    setRecentIds(getRecentSearches());
    onSelect?.(limit);
    inputRef.current?.blur();
  };

  const handleFocus = () => {
    // Clear any pending blur timeout to prevent race condition
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    setIsFocused(true);
  };

  const handleBlur = () => {
    // Delay to allow click on dropdown items
    blurTimeoutRef.current = window.setTimeout(() => setIsFocused(false), 150);
  };

  return (
    <Card className={cn('w-full', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold">
              Conforming Loan Limits
            </CardTitle>
            <CardDescription>{data.year} GSE limits by county</CardDescription>
          </div>
          <Badge variant="outline" className="text-xs">
            {data.year}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search Input */}
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={inputRef}
              role="combobox"
              aria-expanded={showDropdown}
              aria-haspopup="listbox"
              aria-controls="loan-limits-listbox"
              aria-activedescendant={
                highlightedIndex >= 0 && displayItems[highlightedIndex]
                  ? `loan-limit-${displayItems[highlightedIndex].id}`
                  : undefined
              }
              placeholder="Search county or state..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              className="pl-9"
            />
          </div>

          {/* Dropdown Results */}
          {showDropdown && (
            <div
              id="loan-limits-listbox"
              role="listbox"
              ref={parentRef}
              className="absolute z-50 mt-1 w-full max-h-[280px] overflow-auto rounded-md border bg-popover shadow-lg"
            >
              {displayItems.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                  No counties found for "{query}"
                </div>
              ) : (
                <>
                  {/* Section header */}
                  {query.length < MIN_SEARCH_LENGTH && recentLimits.length > 0 && (
                    <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground bg-muted/50">
                      Recent Searches
                    </div>
                  )}

                  {/* Results capped indicator */}
                  {isResultsCapped && (
                    <div className="px-3 py-1.5 text-xs text-muted-foreground bg-muted/30 border-b">
                      Showing {MAX_RESULTS} of {totalMatches.toLocaleString()} results
                    </div>
                  )}

                  {/* Virtualized list */}
                  <div
                    style={{
                      height: `${rowVirtualizer.getTotalSize()}px`,
                      width: '100%',
                      position: 'relative'
                    }}
                  >
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                      const limit = displayItems[virtualRow.index];
                      const isRecent = query.length < MIN_SEARCH_LENGTH;
                      const isHighlighted = highlightedIndex === virtualRow.index;
                      return (
                        <div
                          key={limit.id}
                          id={`loan-limit-${limit.id}`}
                          role="option"
                          aria-selected={isHighlighted}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: `${virtualRow.size}px`,
                            transform: `translateY(${virtualRow.start}px)`
                          }}
                          className={cn(
                            'flex items-center justify-between px-3 py-2 cursor-pointer transition-colors',
                            isHighlighted ? 'bg-accent' : 'hover:bg-accent'
                          )}
                          onMouseDown={() => handleSelect(limit)}
                          onMouseEnter={() => setHighlightedIndex(virtualRow.index)}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {isRecent ? (
                              <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            ) : (
                              <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            )}
                            <span className="truncate text-sm">
                              {limit.county}, {limit.state}
                            </span>
                          </div>
                          {limit.isHighCost && (
                            <Badge
                              variant="secondary"
                              className="ml-2 shrink-0 bg-amber-100 text-amber-800 text-xs dark:bg-amber-900 dark:text-amber-200"
                            >
                              High Cost
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Hint text when focused but no query */}
          {isFocused && query.length > 0 && query.length < MIN_SEARCH_LENGTH && (
            <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover px-3 py-3 shadow-lg">
              <p className="text-sm text-muted-foreground text-center">
                Type at least {MIN_SEARCH_LENGTH} characters to search
              </p>
            </div>
          )}
        </div>

        {/* Results Display */}
        {selectedLimit ? (
          <div className="rounded-lg border bg-muted/30 p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                <span className="font-medium">
                  {selectedLimit.county}, {selectedLimit.state}
                </span>
              </div>
              {selectedLimit.isHighCost && (
                <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                  <TrendingUp className="mr-1 h-3 w-3" />
                  High Cost Area
                </Badge>
              )}
            </div>

            {/* Loan Limits Grid */}
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="rounded-md bg-background p-2">
                <div className="text-xs text-muted-foreground">1-Unit</div>
                <div className="font-semibold text-sm">
                  {formatCurrency(selectedLimit.oneUnit)}
                </div>
              </div>
              <div className="rounded-md bg-background p-2">
                <div className="text-xs text-muted-foreground">2-Unit</div>
                <div className="font-semibold text-sm">
                  {formatCurrency(selectedLimit.twoUnit)}
                </div>
              </div>
              <div className="rounded-md bg-background p-2">
                <div className="text-xs text-muted-foreground">3-Unit</div>
                <div className="font-semibold text-sm">
                  {formatCurrency(selectedLimit.threeUnit)}
                </div>
              </div>
              <div className="rounded-md bg-background p-2">
                <div className="text-xs text-muted-foreground">4-Unit</div>
                <div className="font-semibold text-sm">
                  {formatCurrency(selectedLimit.fourUnit)}
                </div>
              </div>
            </div>

            {/* Base Limit Reference */}
            <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
              <span>
                FIPS: {selectedLimit.fipsState}-{selectedLimit.fipsCounty}
              </span>
              <span>Base 1-Unit: {formatCurrency(data.baseLimit)}</span>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed bg-muted/20 p-6 text-center">
            <MapPin className="mx-auto h-8 w-8 text-muted-foreground/50" />
            <p className="mt-2 text-sm text-muted-foreground">
              Search for a county to view loan limits
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
